import { all, put, call, takeLatest } from '@redux-saga/core/effects';
import { rejectErrorCodeHelper } from '../../util/helpers/rejectErrorCodeHelper';
import history from '../utils/history';

import {
  authenticateUserError,
  authenticateUserSuccess,
  createPlainUserError,
  createPlainUserSuccess,
  createUserError,
  createUserSuccess,
  fetchDecryptedEmailError,
  fetchDecryptedEmailSuccess,
  fetchEncryptedEmailError,
  fetchEncryptedEmailSuccess,
  fetchUsernamesSuccess,
  generateTokenSuccess,
  postPasswordForgottenRequestError,
  postPasswordForgottenRequestSuccess,
  postResetPasswordError,
  postResetPasswordSuccess,
  refreshTokensError,
  refreshTokensSuccess,
  resetUser,
  setBasicAccount,
} from '../actions/user/userActions';
import {
  CREATE_USER,
  AUTHENTICATE_USER,
  LOGOUT_USER,
  REFRESH_TOKENS,
  PASSWORD_FORGOTTEN_REQUEST,
  RESET_PASSWORD_REQUEST,
  GENERATE_TOKEN,
  GET_BASIC_ACCOUNT,
  USERNAMES_FETCH,
  CREATE_PLAIN_USER,
  ENCRYPTED_EMAIL_FETCH,
  DECRYPTED_EMAIL_FETCH,
} from '../actions/user/userActionConstants';
import {
  authenticateUserRequest,
  createUserRequest,
  generateTokenRequest,
  getBasicAccountRequest,
  postForgottenPasswordRequest,
  postResetPasswordRequest,
  refreshTokensRequest,
  getUsernames,
  createPlainUserRequest,
  encryptEmailRequest,
  decryptEmailRequest,
} from '../../request/userRequests';

import { addHeaderToken, removeHeaderToken } from '../../request';
import { JWT_REFRESH_TOKEN, JWT_TOKEN } from '../../constants/localStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { resetApplication } from '../actions/application/applicationActions';
import {
  ACCOUNT_TYPE,
  APPLICATION_TYPE,
  BASIC_ACCOUNT_QUERY_DATA,
  EMAIL,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  IS_UPGRADE_FLOW,
  REGISTRATION_FLOW_TYPE,
  SALES_ASSISTED_QUERY_DATA,
  USERNAMES,
} from '../../constants/sessionStorage';
import {
  CREATE_NEW_APPLICATION,
  REGISTRATION_FLOW_TYPES,
} from '../../util/constants';
import { PAGES } from '../../constants/pages';

function* createUser({ payload }) {
  try {
    const { data } = yield call(createUserRequest, payload.data);
    const { JwtToken, JwtRefreshToken } = data;
    if (JwtToken) {
      yield call(storeInSessionStorage, JWT_TOKEN, JwtToken);
      yield call(storeInSessionStorage, JWT_REFRESH_TOKEN, JwtRefreshToken);
      addHeaderToken(JwtToken);
    }

    yield put(createUserSuccess(data));
    yield put(createUserError(''));
    yield call(payload.onSuccess);
  } catch (error) {
    if (error?.response?.status === 302 && payload?.onRedirection) {
      yield call(payload.onRedirection(payload.data.Email));
      return;
    }
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(createUserError(errorMessage));
    yield call(payload.onError, {
      type: 'manual',
      message: errorMessage,
    });
  }
}

function* authenticateUser({ payload }) {
  try {
    const { data } = yield call(authenticateUserRequest, payload.data);
    const { JwtToken, JwtRefreshToken } = data;
    if (JwtToken) {
      yield call(storeInSessionStorage, JWT_TOKEN, JwtToken);
      yield call(storeInSessionStorage, JWT_REFRESH_TOKEN, JwtRefreshToken);
      addHeaderToken(JwtToken);
    }

    yield put(authenticateUserSuccess(data));
    yield put(authenticateUserError(''));
    yield call(payload.onSuccess);
  } catch (error) {
    if (
      error.response &&
      error.response.status === 302 &&
      payload.redirectToV1
    ) {
      yield call(payload.redirectToV1, payload.data.Email);
      return;
    }
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(authenticateUserError(errorMessage));
    yield call(payload.onError, {
      type: 'manual',
      message: errorMessage,
    });
  }
}

function* logoutUser() {
  try {
    yield put(resetUser());
    yield put(resetApplication());
    if (
      retrieveFromSessionStorage(REGISTRATION_FLOW_TYPE) ===
      REGISTRATION_FLOW_TYPES.AIP
    ) {
      history.replace(PAGES.AIP);
    } else {
      history.replace(PAGES.BASE);
    }
    removeFromSessionStorage(JWT_TOKEN);
    removeFromSessionStorage(JWT_REFRESH_TOKEN);
    removeFromSessionStorage(IS_UPGRADE_FLOW);
    removeFromSessionStorage(BASIC_ACCOUNT_QUERY_DATA);
    removeFromSessionStorage(ACCOUNT_TYPE);
    removeFromSessionStorage(IS_SALES_ASSESSTED_REGISTRATION_FLOW);
    removeFromSessionStorage(SALES_ASSISTED_QUERY_DATA);
    // check if this affects something
    removeFromSessionStorage(APPLICATION_TYPE);
    removeFromSessionStorage(CREATE_NEW_APPLICATION);
    removeFromSessionStorage(EMAIL);
    yield call(removeHeaderToken);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

function* refreshTokens({ payload }) {
  try {
    const { data } = yield call(refreshTokensRequest, { data: payload.data });
    const { JwtToken, JwtRefreshToken } = data;
    if (JwtToken) {
      yield put(refreshTokensSuccess(data));
      yield call(storeInSessionStorage, JWT_REFRESH_TOKEN, JwtRefreshToken);
      yield call(storeInSessionStorage, JWT_TOKEN, JwtToken);
      addHeaderToken(JwtToken);
    }
    if (payload?.onSuccess) {
      yield call(payload?.onSuccess);
    }
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(refreshTokensError(errorMessage));
    yield call(logoutUser);
  }
}
function* postForgottenPassword({ payload }) {
  try {
    const { data } = yield call(postForgottenPasswordRequest, payload.data);
    yield put(postPasswordForgottenRequestSuccess(data));
    yield put(postPasswordForgottenRequestError(''));
    yield call(payload?.handleSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield call(payload?.handleError, errorMessage);
    yield put(postPasswordForgottenRequestError(errorMessage));
  }
}

function* postResetPassword({ payload }) {
  try {
    const { data } = yield call(postResetPasswordRequest, payload.data);
    yield put(postResetPasswordSuccess(data));
    yield put(postResetPasswordError(''));
    yield call(payload.handleSuccess);
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield call(payload.handleError, errorMessage);
    yield put(postResetPasswordError(errorMessage));
  }
}

export function* generateToken({ payload }) {
  try {
    const { token } = payload;
    const { data } = yield call(generateTokenRequest, token);
    const { JwtToken, JwtRefreshToken } = data;
    if (JwtToken && JwtRefreshToken) {
      storeInSessionStorage(JWT_TOKEN, JwtToken);
      storeInSessionStorage(JWT_REFRESH_TOKEN, JwtRefreshToken);
      // history.push(PAGES.CHOOSE_APPLICATION.MAIN);
      yield call(payload.onSuccess, JwtToken);
      // yield call(onSuccess, JwtToken);
    }
    yield put(generateTokenSuccess(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

export function* getBasicAccount({ payload }) {
  try {
    const { data } = yield call(getBasicAccountRequest, {
      applicationUid: payload.applicationUid,
      accountUid: payload.accountUid,
    });
    if (data) {
      yield put(setBasicAccount(data));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
}

function* fetchUsernames(payload) {
  const { email, setIsLoading, onSuccess, onError, encryptV1Email } = payload;
  try {
    const response = yield call(getUsernames, payload);
    storeInSessionStorage(EMAIL, email);
    if (response && response.data && !!response.data.Usernames.length) {
      yield call(storeInSessionStorage, USERNAMES, response?.data?.Usernames);
      if (onSuccess) {
        yield call(onSuccess);
      }
      return yield put(fetchUsernamesSuccess(response.data));
    }
    if (!response?.data?.Usernames?.length) {
      yield call(onError);
      return;
    }
  } catch (error) {
    if (error.response && error.response.status === 302) {
      yield call(encryptV1Email, email);
      return;
    }
  } finally {
    if (setIsLoading) {
      yield call(setIsLoading, false);
    }
  }
}

function* createPlainUser({ payload }) {
  try {
    const { data } = yield call(createPlainUserRequest, payload.data);
    const { JwtToken, JwtRefreshToken } = data;
    if (JwtToken) {
      yield call(storeInSessionStorage, JWT_TOKEN, JwtToken);
      yield call(storeInSessionStorage, JWT_REFRESH_TOKEN, JwtRefreshToken);
      addHeaderToken(JwtToken);
    }

    yield put(createPlainUserSuccess(data));
    yield put(createPlainUserError(''));
    yield call(payload.onSuccess);
  } catch (error) {
    const errorMessage = rejectErrorCodeHelper(error);
    yield put(createPlainUserError(errorMessage));
    yield call(payload.onError, {
      type: 'manual',
      message: errorMessage,
    });
  }
}
function* fetchEncryptedEmail({ payload }) {
  try {
    const response = yield call(encryptEmailRequest, payload.data);
    yield put(fetchEncryptedEmailSuccess(response.data));
    yield call(payload.redirectToV1, response.data);
    yield put(fetchEncryptedEmailError(''));
    if (payload.goToLoginPage) {
      yield call(payload.goToLoginPage);
    }
  } catch (error) {
    const e = rejectErrorCodeHelper(error);
    yield put(fetchEncryptedEmailError(e));
  }
}
function* fetchDecryptedEmail({ payload }) {
  try {
    const response = yield call(decryptEmailRequest, payload.data);
    yield put(fetchDecryptedEmailSuccess(response.data));
    yield put(fetchDecryptedEmailError(''));
    if (payload.fetchUsernamesCall) {
      yield call(payload.fetchUsernamesCall, { email: response.data });
    }
  } catch (error) {
    const e = rejectErrorCodeHelper(error);
    yield put(fetchDecryptedEmailError(e));
  }
}
export default function* userSaga() {
  return yield all([
    takeLatest(CREATE_USER, createUser),
    takeLatest(AUTHENTICATE_USER, authenticateUser),
    takeLatest(REFRESH_TOKENS, refreshTokens),
    takeLatest(LOGOUT_USER, logoutUser),
    takeLatest(PASSWORD_FORGOTTEN_REQUEST, postForgottenPassword),
    takeLatest(RESET_PASSWORD_REQUEST, postResetPassword),
    takeLatest(GENERATE_TOKEN, generateToken),
    takeLatest(GET_BASIC_ACCOUNT, getBasicAccount),
    takeLatest(USERNAMES_FETCH, fetchUsernames),
    takeLatest(CREATE_PLAIN_USER, createPlainUser),
    takeLatest(ENCRYPTED_EMAIL_FETCH, fetchEncryptedEmail),
    takeLatest(DECRYPTED_EMAIL_FETCH, fetchDecryptedEmail),
  ]);
}
